/**
 * Landing page.
 *
 * Two honest halves: the screens that exist and work, and the dashboards that do
 * not exist yet, each named with the ticket that builds it. Both are filtered by
 * the shared permission matrix, so what a role can reach is visible at a glance —
 * which is the part of KAN-46 that has to be demonstrable end to end.
 *
 * No figures are shown. Placeholder totals on a finance dashboard get quoted in
 * meetings; a card that says the data is not connected yet does not.
 */
import { Link } from 'react-router-dom'
import { ROLE_LABELS, type Permission } from '@accounting/shared'
import { useSession } from '../lib/session'
import { Badge, RoleBadge } from '../ui/Badge'
import { Card, CardBody } from '../ui/Card'
import { PageHeader } from '../ui/PageHeader'
import { ChevronIcon } from '../ui/icons'

interface Section {
  title: string
  summary: string
  /** Any one of these grants makes the section reachable. */
  permissions?: readonly Permission[]
}

interface Tool extends Section {
  to: string
}

interface Planned extends Section {
  ticket: string
}

const TOOLS: Tool[] = [
  {
    to: '/executive',
    title: 'Executive dashboard',
    summary:
      'Every figure the firm is measured on in one place — cash, receivables, WIP, trust and payables — each drilling into the module it came from, and each saying how fresh it is.',
    permissions: ['firm_financials.view'],
  },
  {
    to: '/weekly',
    title: 'Weekly financial dashboard',
    summary:
      'The week in one page: the three biggest concerns, cash and the next two weeks of deposits and payments, receivables, budget, production and trust.',
    permissions: ['firm_financials.view'],
  },
  {
    to: '/alerts',
    title: 'Exception alerts',
    summary:
      'Thresholds crossed across trust, refunds, payables and billing — raised once per condition rather than once per check, and delivered to the notification centre, email and Slack.',
    permissions: ['alerts.view'],
  },
  {
    to: '/billing-exceptions',
    title: 'Billing exceptions',
    summary:
      'Rate differences against the retainer, incomplete or duplicated entries, and time recorded with no matter.',
    permissions: ['billing_exceptions.view_all', 'billing_exceptions.view_own_matters'],
  },
  {
    to: '/billing-forecast',
    title: 'Billing forecast',
    summary:
      'Upcoming billings from work in progress, projected trust transfers, and expected cash.',
    permissions: ['firm_financials.view'],
  },
  {
    to: '/accounts-receivable',
    title: 'Accounts receivable',
    summary:
      'Outstanding invoices by aging bucket, client, matter and attorney, with collection risk and the invoices behind every figure.',
    permissions: ['ar.view'],
  },
  {
    to: '/refunds',
    title: 'Client refunds',
    summary:
      'Money returned to clients by reason and approver, how much of it the firm caused, and the invoice or trust movement behind each one.',
    permissions: ['refunds.view'],
  },
  {
    to: '/trust-funding',
    title: 'Trust funding',
    summary:
      'Matters running low on client money, already overdrawn, or facing a hearing they cannot pay for — with the full IOLTA ledger behind each one.',
    permissions: ['trust_ledger.view'],
  },
  {
    to: '/users',
    title: 'Users',
    summary: 'Provision accounts, change roles, deactivate people who have left.',
    permissions: ['users.manage'],
  },
  {
    to: '/roles',
    title: 'Roles & permissions',
    summary: 'The matrix the API enforces, in full.',
  },
  {
    to: '/audit-log',
    title: 'Audit log',
    summary: 'Every change to financial data, with who made it and what it was before.',
    permissions: ['audit_log.view'],
  },
  {
    to: '/integrations',
    title: 'Integrations',
    summary: 'Clio and QuickBooks connection state, and per-resource sync health.',
    permissions: ['integrations.manage'],
  },
]

const PLANNED: Planned[] = [
  {
    title: 'Productivity by attorney',
    summary:
      'Billed and collected against target, per attorney. The executive dashboard already carries the firm-wide figures; this is the breakdown beneath them.',
    ticket: 'KAN-36',
    permissions: ['firm_financials.view'],
  },
  {
    /**
     * Still unbuilt, and the executive dashboard says so in its own words: the
     * cash position, cash flow forecast and payroll tiles all render as
     * explicitly unavailable against this ticket rather than as zero.
     */
    title: 'Cash flow',
    summary: 'Position, upcoming vendor obligations and the rolling forecast.',
    ticket: 'KAN-49',
    permissions: ['cash_flow.view'],
  },
]

export function Overview() {
  const { user, may } = useSession()
  if (!user) return null

  const allowed = (section: Section) => !section.permissions || section.permissions.some(may)

  const tools = TOOLS.filter(allowed)
  const planned = PLANNED.filter(allowed)
  const withheld = [...TOOLS, ...PLANNED].length - tools.length - planned.length

  return (
    <>
      <PageHeader
        title={`Good to see you, ${firstName(user.fullName)}`}
        description="Finance and practice analytics for Divorce with a Plan."
        actions={<RoleBadge role={user.role} />}
      />

      <section aria-labelledby="available" className="mb-10">
        <h2 id="available" className="mb-3 font-display text-lg font-semibold text-brand-900">
          Available now
        </h2>

        <div className="grid gap-4 sm:grid-cols-2">
          {tools.map((tool) => (
            <Link
              key={tool.to}
              to={tool.to}
              className="group rounded-lg border border-ink-200 bg-white p-5 shadow-[0_1px_2px_rgba(21,26,108,0.04)] transition-colors hover:border-brand-300 hover:bg-brand-50/40"
            >
              <span className="flex items-center justify-between gap-2">
                <span className="font-display text-base font-semibold text-brand-900">
                  {tool.title}
                </span>
                <ChevronIcon className="shrink-0 text-brand-500 transition-transform group-hover:translate-x-0.5" />
              </span>
              <span className="mt-1 block text-sm text-ink-600">{tool.summary}</span>
            </Link>
          ))}
        </div>
      </section>

      {planned.length > 0 && (
        <section aria-labelledby="planned">
          <h2 id="planned" className="mb-1 font-display text-lg font-semibold text-brand-900">
            Not built yet
          </h2>
          <p className="mb-3 max-w-2xl text-sm text-ink-600">
            The data model, sync framework and permissions behind these are in place. The dashboards
            themselves are the next tickets, and the Clio and QuickBooks connections they read from
            are waiting on OAuth credentials.
          </p>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {planned.map((section) => (
              <Card key={section.title}>
                <CardBody>
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-medium text-ink-900">{section.title}</h3>
                    <Badge tone="neutral">{section.ticket}</Badge>
                  </div>
                  <p className="mt-1 text-sm text-ink-600">{section.summary}</p>
                </CardBody>
              </Card>
            ))}
          </div>
        </section>
      )}

      {withheld > 0 && (
        <p className="mt-8 max-w-2xl border-t border-ink-200 pt-5 text-xs leading-relaxed text-ink-600">
          {withheld} further {withheld === 1 ? 'section is' : 'sections are'} not shown, because the{' '}
          {ROLE_LABELS[user.role]} role does not hold the grants they require. They are refused by the
          API as well as hidden here — see{' '}
          <Link to="/roles" className="text-brand-700 underline">
            roles &amp; permissions
          </Link>
          .
        </p>
      )}
    </>
  )
}

function firstName(fullName: string): string {
  return fullName.trim().split(/\s+/)[0] ?? fullName
}
